import { assertAdmin } from "@/lib/admin/auth.server";
/**
 * Server functions para gerenciar campanhas de cupom.
 *  - getActiveCampaignFn: público (usado em loaders SSR de /pos)
 *  - activateCampaign / deactivateCampaign / listCampaignHistory: apenas admin
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  ACTIVE_CAMPAIGN_KEY,
  CAMPAIGN_PRESETS,
  type ActiveCampaign,
} from "@/lib/checkout/campanhas";
import { readActiveCampaignServer } from "@/lib/checkout/active-campaign.server";


/** Público — usado no loader SSR de /pos. */
export const getActiveCampaignFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<ActiveCampaign | null> => {
    return readActiveCampaignServer();
  },
);

const ActivateSchema = z.union([
  z.object({ kind: z.literal("preset"), slug: z.string().min(1) }),
  z.object({
    kind: z.literal("custom"),
    cupom: z
      .string()
      .trim()
      .min(2)
      .max(40)
      .regex(/^[A-Z0-9_-]+$/i, "Use apenas letras, números, _ e -"),
    nome: z.string().trim().min(1).max(80).optional(),
  }),
]);

export const activateCampaign = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ActivateSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "/admin/campanhas");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let campaign: ActiveCampaign;
    if (data.kind === "preset") {
      const preset = CAMPAIGN_PRESETS.find((p) => p.slug === data.slug);
      if (!preset) throw new Error(`Preset "${data.slug}" não encontrado`);
      campaign = {
        slug: preset.slug,
        nome: preset.nome,
        cupom: preset.cupom.toUpperCase(),
        cupons: preset.cupons,
        mirrorRoute: preset.mirrorRoute,
        isCustom: false,
        ativadoEm: new Date().toISOString(),
      };
    } else {
      const cupom = data.cupom.trim().toUpperCase();
      const slug = cupom.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      campaign = {
        slug,
        nome: data.nome?.trim() || cupom,
        cupom,
        isCustom: true,
        ativadoEm: new Date().toISOString(),
      };
    }

    const { error: upErr } = await supabaseAdmin
      .from("site_config")
      .upsert(
        {
          key: ACTIVE_CAMPAIGN_KEY,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          value: campaign as any,
          updated_by: context.userId,
        },
        { onConflict: "key" },
      );
    if (upErr) throw new Error(upErr.message);

    await supabaseAdmin.from("campaign_activation_log").insert({
      action: "ativar",
      campaign_slug: campaign.slug,
      campaign_nome: campaign.nome,
      cupom: campaign.cupom,
      is_custom: campaign.isCustom,
      mirror_route: campaign.mirrorRoute ?? null,
      metadata: { source: data.kind },
      created_by: context.userId,
    });

    return { ok: true, campaign };
  });

export const deactivateCampaign = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId, "/admin/campanhas");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const current = await readActiveCampaignServer();

    const { error } = await supabaseAdmin
      .from("site_config")
      .delete()
      .eq("key", ACTIVE_CAMPAIGN_KEY);
    if (error) throw new Error(error.message);

    if (current) {
      await supabaseAdmin.from("campaign_activation_log").insert({
        action: "desativar",
        campaign_slug: current.slug,
        campaign_nome: current.nome,
        cupom: current.cupom,
        is_custom: current.isCustom,
        mirror_route: current.mirrorRoute ?? null,
        metadata: {},
        created_by: context.userId,
      });
    }
    return { ok: true };
  });

export type CampaignLogRow = {
  id: string;
  action: "ativar" | "desativar";
  campaign_slug: string;
  campaign_nome: string;
  cupom: string;
  is_custom: boolean;
  mirror_route: string | null;
  created_at: string;
  created_by: string | null;
};

export const listCampaignHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<CampaignLogRow[]> => {
    await assertAdmin(context.userId, "/admin/campanhas");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("campaign_activation_log")
      .select("id, action, campaign_slug, campaign_nome, cupom, is_custom, mirror_route, created_at, created_by")
      .order("created_at", { ascending: false })
      .limit(50);
    return (data ?? []) as CampaignLogRow[];
  });
